import type { GetServerSidePropsContext } from 'next';
import type { GlobalHeaderProps } from '@eventbrite/consumer-header.global-header';
import { getGlobalHeaderServerSideProps } from '@eventbrite/consumer-header.global-header/server';
import type { GlobalFooterProps } from '@eventbrite/legacy-infra.site-footer';
import { getGlobalFooterServerSideProps } from '@eventbrite/legacy-infra.site-footer/server';
import { extractHostTLD, SUPPORTED_TLDS } from '@eventbrite/webapp-locales';

import { BASE_PATH } from '@/lib/constants';
import type { IncomingMessage } from 'http';
import { createDefaultLocaleInfo, type DefaultLocaleInfo } from '@/lib/localeInfo';

const DEFAULT_SERVER_URL = 'https://www.eventbrite.com';
const FIXTURES_ENV = 'COVID19_FIXTURES';

export interface Covid19PageProps {
    globalHeaderProps: GlobalHeaderProps;
    globalFooterProps: GlobalFooterProps;
    localeInfo: DefaultLocaleInfo;
    serverUrl: string;
    isAuthenticated: boolean;
    isBot: boolean;
    // Route params (undefined on the landing / financial-relief index routes).
    country: string | null;
    region: string | null;
}

// Drop every `undefined` leaf so gSSP never throws the "cannot be serialized as
// JSON" 500 at request time (a trap the build/tsc/jest gates all miss).
function jsonSafe<T>(value: T): T {
    return JSON.parse(JSON.stringify(value)) as T;
}

type Headers = Record<string, string | string[] | undefined>;

// The public-facing Eventbrite base domains the `evbqa|evbstage|eventbrite`
// staging/prod regex in webapp-locales recognises. We match on a domain-label
// boundary so lookalikes such as `evil-eventbrite.com` can never pass.
const EB_BASE_DOMAINS = ['eventbrite', 'evbqa', 'evbstage'] as const;

// A syntactically valid Host is a series of DNS labels (letters/digits/hyphen)
// with an optional numeric port. Anything else — userinfo (`user@host`), path
// or query chars, backslashes, extra colons — is rejected outright. This is the
// gate that stops `www.eventbrite.com:80@evil.com` from slipping through:
// stripping the port with `split(':')` would leave the trusted-looking
// `www.eventbrite.com`, yet the retained string resolves (via URL userinfo) to
// `evil.com` once rendered into an href / passed to `location.assign`.
const HOST_SYNTAX = /^[a-z0-9.-]+(:[0-9]+)?$/i;

// Internal origins — the raw API-Gateway/CloudFront-origin hostnames this app is
// actually served from (e.g. `apigw-covid-19-webapp.<hash>.ext.eventbrite.com`)
// — are subdomains of `eventbrite.com` and so would otherwise pass the trust
// check below. They must NOT be used as the public base URL: they sit behind the
// origin-key WAF, so any browser navigation to them (the footer locale switcher
// builds `${serverUrl}/international/change_locale/…`) bypasses CloudFront, never
// gets the `x-eb-origin-key` header injected, and is answered with
// `403 {"message":"Forbidden"}`. The `.ext.` infix and `apigw-` label mark these
// internal hosts; reject them so `getServerUrl` falls back to DEFAULT_SERVER_URL.
const INTERNAL_ORIGIN_INFIX = '.ext.';
const INTERNAL_ORIGIN_PREFIX = 'apigw-';

// A host is trusted only when it is syntactically a bare host[:port] AND is
// exactly `<base>.<tld>` or a subdomain `*.<base>.<tld>` where <base> is a real
// Eventbrite base and <tld> is in the supported allowlist (so `eventbrite.evil.com`
// and `evil-eventbrite.com` are both rejected — `extractHostTLD` alone would
// accept the latter) AND it is not an internal API-GW/CloudFront origin.
function isTrustedEventbriteHost(hostWithoutPort: string): boolean {
    if (
        hostWithoutPort.includes(INTERNAL_ORIGIN_INFIX) ||
        hostWithoutPort.startsWith(INTERNAL_ORIGIN_PREFIX)
    ) {
        return false;
    }
    const tld = extractHostTLD(hostWithoutPort);
    if (!tld || !(tld in SUPPORTED_TLDS)) {
        return false;
    }
    return EB_BASE_DOMAINS.some((base) => {
        const apex = `${base}.${tld}`;
        return hostWithoutPort === apex || hostWithoutPort.endsWith(`.${apex}`);
    });
}

// `x-forwarded-host` / `x-forwarded-proto` are attacker-controllable and this
// response is served with `public` CDN caching, so echoing them straight into
// the footer links / locale-change URLs would let one crafted request poison
// the shared cache with attacker-controlled hosts (open redirect / phishing
// links) for every other visitor. Only trust a host that is a real Eventbrite
// domain; otherwise fall back to the canonical URL. Exported for unit tests.
export function getServerUrl(headers: Headers): string {
    const rawHost = headers['x-forwarded-host'] ?? headers.host;
    if (!rawHost) {
        return DEFAULT_SERVER_URL;
    }
    const hostname = (Array.isArray(rawHost) ? rawHost[0] : rawHost)?.trim();
    if (!hostname) {
        return DEFAULT_SERVER_URL;
    }

    // Reject anything that is not a bare `host[:port]` before trusting it — a
    // value like `www.eventbrite.com:80@evil.com` would otherwise survive the
    // `split(':')` port strip below (leaving the trusted-looking host) yet
    // resolve to `evil.com` once concatenated into a URL.
    if (!HOST_SYNTAX.test(hostname)) {
        return DEFAULT_SERVER_URL;
    }

    // Strip any port before validating against the domain allowlist.
    const hostWithoutPort = hostname.split(':')[0].toLowerCase();
    if (!isTrustedEventbriteHost(hostWithoutPort)) {
        return DEFAULT_SERVER_URL;
    }

    // Never let the header downgrade the scheme; production is always https.
    const proto = headers['x-forwarded-proto'];
    const scheme = (Array.isArray(proto) ? proto[0] : proto)?.trim();
    const safeScheme = scheme === 'http' || scheme === 'https' ? scheme : 'https';
    return `${safeScheme}://${hostname}`;
}

function firstHeader(value: string | string[] | undefined): string {
    if (Array.isArray(value)) {
        return value[0] ?? '';
    }
    return value ?? '';
}

// Minimal crawler detection (eb-ui got `request.is_bot` from Django). Normal
// browsers => false, so the interactive SelectField picker renders.
const BOT_UA = /bot|crawl|spider|slurp|bingpreview|googlebot|duckduckbot|baiduspider/i;

function paramString(value: string | string[] | undefined): string | null {
    if (Array.isArray(value)) {
        return value[0] ?? null;
    }
    return value ?? null;
}

/**
 * Shared getServerSideProps for every covid-19 route. There is NO data fetch —
 * the content is 100% static; the only server work is assembling the shared
 * chrome (header + footer) and reconstructing the SSR context. `country` /
 * `region` come from the route params and select the static content client-side.
 */
export async function getCovid19ServerSideProps(
    context: GetServerSidePropsContext,
) {
    const { req, res, params, resolvedUrl } = context;
    const headers = req.headers as Headers;
    const serverUrl = getServerUrl(headers);

    const country = paramString(params?.country);
    const region = paramString(params?.region);

    // Derive the header path from the actual request URL (basePath-relative,
    // as Next strips the basePath from `resolvedUrl`) so the two param-less
    // routes — `/covid-19/` and `/covid-19/financial-relief/` — are
    // distinguished. Rebuilding solely from `country`/`region` collapses both
    // onto `/covid-19/`, giving GlobalHeader the wrong path for the relief
    // landing page.
    const pathname = resolvedUrl.split('?')[0];
    const path = `${BASE_PATH}${pathname}`;

    const emptyHeader: GlobalHeaderProps = {
        env: { serverUrl },
        request: { path },
    };
    const emptyFooter: GlobalFooterProps = {
        thirdFooterColumnName: 'Find Events',
        thirdFooterColumn: [],
        popularSearches: [],
        eventsToPlan: [],
        showMinimalLinks: false,
        impressumHelpLink: '',
    };

    let globalHeaderProps: GlobalHeaderProps = emptyHeader;
    let globalFooterProps: GlobalFooterProps = emptyFooter;

    // Under the fixtures flag skip the remote chrome calls so the real page
    // renders with no creds/VPN; otherwise fetch both and degrade each to empty.
    if (process.env[FIXTURES_ENV] !== '1') {
        const [headerResult, footerResult] = await Promise.allSettled([
            getGlobalHeaderServerSideProps(context),
            getGlobalFooterServerSideProps(context),
        ]);
        if (headerResult.status === 'fulfilled') {
            // Keep the upstream env (real serverUrl / localeInfo / organize &
            // help URLs) and isHomePage, but restore our basePath-relative
            // `path`. The chrome helper derives `request.path` from `req.url`,
            // which under the Pages Router does not distinguish `/covid-19/`
            // from `/covid-19/financial-relief/` the way our derived path does.
            globalHeaderProps = {
                ...headerResult.value,
                request: {
                    ...headerResult.value.request,
                    path,
                },
            };
        }
        if (footerResult.status === 'fulfilled') {
            globalFooterProps = footerResult.value;
        }
    }

    const trustedContext = withTrustedHostHeaders(context, serverUrl);
    const localeInfo = createDefaultLocaleInfo(trustedContext.req);
    // TEMP DIAGNOSTIC (remove after i18n-host investigation): expose the raw
    // host-bearing headers the app actually received.
    res.setHeader(
        'x-debug-host-headers',
        JSON.stringify({
            eb_host: headers['eb-host'] ?? null,
            xfh: headers['x-forwarded-host'] ?? null,
            host: headers['host'] ?? null,
            resolvedServerUrl: serverUrl,
            localeInfoLocale: (localeInfo as unknown as Record<string, unknown>).locale ?? null,
        }),
    );
    const isAuthenticated = Boolean(firstHeader(headers['eb-user-id']));
    const isBot = BOT_UA.test(firstHeader(headers['user-agent']));

    // Static informational page — cache aggressively at the CDN. The page body
    // is identical for everyone, but the assembled chrome is request-derived:
    // `serverUrl` comes from the Host / x-forwarded-host headers (validated
    // above), and the localized GlobalHeader/GlobalFooter chrome comes from the
    // TLD (eb-host), the locale cookie (lang/eblang), and Accept-Language.
    // Advertise `Vary` on every input that shapes the HTML so a shared edge
    // cache never serves one visitor's chrome (or host-derived links) to
    // another.
    res.setHeader(
        'Vary',
        'eb-host, x-forwarded-host, host, x-forwarded-proto, Cookie, Accept-Language',
    );
    res.setHeader(
        'Cache-Control',
        'public, max-age=0, s-maxage=3600, stale-while-revalidate=86400, stale-if-error=86400',
    );

    return {
        props: jsonSafe<Covid19PageProps>({
            globalHeaderProps,
            globalFooterProps,
            localeInfo,
            serverUrl,
            isAuthenticated,
            isBot,
            country,
            region,
        }),
    };
}

/**
 * A context whose request carries only the VALIDATED host in every
 * host-bearing header (open-source/trending pattern): everything derived from
 * host headers (localeInfo, TLD locale) is anchored to the sanitized
 * serverUrl — the raw request Host in prod is the internal apigw origin.
 */
export function withTrustedHostHeaders<C extends { req: IncomingMessage }>(
    context: C,
    serverUrl: string,
): C {
    const trustedHost = new URL(serverUrl).host;
    const safeReq: IncomingMessage = Object.create(context.req, {
        headers: {
            value: {
                ...context.req.headers,
                'eb-host': trustedHost,
                'x-forwarded-host': trustedHost,
                host: trustedHost,
                'x-forwarded-proto': 'https',
            },
            enumerable: true,
        },
    });
    return { ...context, req: safeReq };
}
